/**
 * <s-welcome-offer> — first-visit email sign-up offer.
 *
 * Wraps an <s-dialog><dialog> holding a customer (newsletter) form. Opens once per
 * visitor after a delay, on desktop exit intent, or immediately with #welcome in the
 * URL (preview). After a successful sign-up the page reloads with
 * `customer_posted=true`; the element reopens in its success state to reveal the code.
 *
 * Attributes:
 *   delay        — ms before auto-open. Default 8000.
 *   snooze-days  — days to stay hidden after the shopper closes it. Default 14.
 *   suppress     — never auto-open (e.g. logged-in customer already subscribed).
 *
 * Commands (on the dialog):
 *   --copy — copy the text of the element named by event.source aria-controls.
 */
const SUBSCRIBED_KEY = 'etz-welcome-subscribed';
const SNOOZE_KEY = 'etz-welcome-snooze';
const PENDING_KEY = 'etz-welcome-pending';

function read(storage, key) {
  try { return storage.getItem(key); } catch { return null; }
}
function write(storage, key, value) {
  try { storage.setItem(key, value); } catch { /* storage blocked */ }
}
function remove(storage, key) {
  try { storage.removeItem(key); } catch { /* storage blocked */ }
}

class SWelcomeOffer extends HTMLElement {
  #dialog;
  #ac;
  #timer;
  #success = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog.addEventListener('submit', () => write(sessionStorage, PENDING_KEY, '1'), opts);
    this.#dialog.addEventListener('close', () => this.#onClose(), opts);
    this.#dialog.addEventListener('command', (e) => {
      if (e.command !== '--copy') return;
      e.preventDefault();
      this.#copy(e.source);
    }, opts);

    const pending = read(sessionStorage, PENDING_KEY) === '1';
    const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';

    if (pending) {
      remove(sessionStorage, PENDING_KEY);
      if (posted) {
        write(localStorage, SUBSCRIBED_KEY, '1');
        this.#success = true;
        this.dataset.state = 'success';
      }
      // Success reveals the code; otherwise reopen so the shopper sees the form error.
      this.#open();
      return;
    }

    if (location.hash === '#welcome') {
      this.#open();
      return;
    }

    if (this.hasAttribute('suppress')) return;
    if (read(localStorage, SUBSCRIBED_KEY) === '1') return;
    const snoozedUntil = Number(read(localStorage, SNOOZE_KEY) || 0);
    if (snoozedUntil > Date.now()) return;

    const delay = Number(this.getAttribute('delay') || 8000);
    this.#timer = setTimeout(() => this.#open(), delay);

    // Desktop exit intent: pointer leaves through the top of the window.
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', (e) => {
        if (e.clientY <= 0) this.#open();
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #open() {
    clearTimeout(this.#timer);
    if (this.#dialog.open) return;
    // Never stack over the cart, menu or search.
    if (document.querySelector('dialog[open]')) {
      this.#timer = setTimeout(() => this.#open(), 4000);
      return;
    }
    this.#dialog.showModal();
  }

  #onClose() {
    if (this.#success) return;
    const days = Number(this.getAttribute('snooze-days') || 14);
    write(localStorage, SNOOZE_KEY, String(Date.now() + days * 86400000));
  }

  async #copy(source) {
    if (!(source instanceof HTMLElement)) return;
    const target = document.getElementById(source.getAttribute('aria-controls') || '');
    if (!target) return;
    const label = source.querySelector('[data-copy-label]');
    try {
      await navigator.clipboard.writeText(target.textContent.trim());
      if (label && source.dataset.copiedLabel) {
        const original = label.textContent;
        label.textContent = source.dataset.copiedLabel;
        setTimeout(() => { label.textContent = original; }, 2000);
      }
    } catch {
      const range = document.createRange();
      range.selectNodeContents(target);
      const selection = getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }
}

if (!customElements.get('s-welcome-offer')) customElements.define('s-welcome-offer', SWelcomeOffer);
